<script setup lang="ts">
import { logIn, logOut, print, arrowBack } from "ionicons/icons";
import {
  IonContent,
  IonPage,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonFooter,
  IonGrid,
  IonFab,
  IonFabButton,
  IonBadge,
  IonButton,
  IonLoading,
  IonIcon,
  onIonViewWillEnter,
  useIonRouter,
} from "@ionic/vue";
import { useContextStore } from "@/stores/context";
import { computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { IVisitor } from "@/models/visitor";
import { getVisitorById } from "@/api/visitors";
import { pencil } from "ionicons/icons";
import {
  getSessionRegistrations,
  getSessions,
  sessionRegistration,
  updateSessionRegistration,
} from "@/api/sessions";
import { ISession, ISessionRegistration } from "@/models/session";
import { Printer, PrintOptions } from "@awesome-cordova-plugins/printer";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { FileOpener } from "@ionic-native/file-opener";
import axios from "axios";
import { getDefaultLabelPdf } from "@/api/label";
import { useUserStore } from "@/stores/user";
import { useToasters } from "@/composition/toasters";
import { handleError } from "@/views";
import { useWindowWidth } from "@/composition/window";
import { useAppStore } from "@/stores/app";

const { isMobile } = useWindowWidth();
const contextStore = useContextStore();
const router = useIonRouter();
const toasters = useToasters();
const route = useRoute();
const appStore = useAppStore();

const state = reactive({
  visitor: {} as IVisitor,
  checkInRegistration: undefined as ISessionRegistration | undefined,
  loading: false,
});

const visitor_id = parseInt(route.params.visitor_id as string, 10);

let session = {} as ISession;

onIonViewWillEnter(() => {
  getVisitor();
});

const getVisitor = () => {
  state.loading = true;
  getVisitorById(visitor_id)
    .then((response) => {
      state.visitor = response.data;
      state.loading = false;
      getSessions({
        event: contextStore.event.id,
        search: "check-in",
      })
        .then((response) => {
          session = response.data.results[0];
          if (session) {
            getSessionRegistrations({
              event: contextStore.event.id,
              visitor: state.visitor.id,
              session: session.id,
            })
              .then((response) => {
                state.checkInRegistration = response.data.results[0];
                state.loading = false;
              })
              .catch(() => {
                state.loading = false;
              });
          }
        })
        .catch((err) => {
          handleError(err);
          state.loading = false;
        });
    })
    .catch((err) => {
      handleError(err);
      state.loading = false;
    });
};

const printTag = () => {
  state.loading = true;

  getDefaultLabelPdf({ event: contextStore.event.id })
    .then((response) => {
      const userStore = useUserStore();
      axios
        .get(
          `${response.data.url}?target_type=Attending&target_id=${visitor_id}&ci=true`,
          {
            headers: {
              // tslint:disable-next-line: object-literal-key-quotes
              Authorization: `Token ${userStore.token}`,
              "content-type": "application/pdf",
              "Accept-Language": "en",
            },
            responseType: "arraybuffer",
          }
        )
        .then((response) => {
          Printer.isAvailable()
            .then(() => {
              const reader = new FileReader();
              reader.readAsDataURL(
                new Blob([response.data], { type: "application/pdf" })
              );
              reader.onloadend = function () {
                const base64data = reader.result as string;
                const filepath = `beamian-check-in_label--${Date.now()}.pdf`;
                Filesystem.writeFile({
                  path: filepath,
                  data: base64data,
                  directory: Directory.Documents,
                  // encoding: Encoding.UTF16,
                })
                  .then((result) => {
                    FileOpener.open(result.uri, "application/pdf")
                      .then(() => {
                        state.loading = false;
                      })
                      .catch(() => {
                        toasters.addWarning(
                          "Could not open file, attempting to print directly"
                        );
                        let options: PrintOptions = {
                          // orientation: "landscape",
                          monochrome: true,
                          margin: false,
                          autoFit: false,
                          paper: {
                            width: "62",
                            height: "30",
                            name: "Roll24Inch",
                            length: "62",
                          },
                        };
                        Printer.print(result.uri, options)
                          .then(
                            () => {
                              state.loading = false;
                            },
                            () => {
                              toasters.addError(
                                "Label printing failed, please try again."
                              );
                              state.loading = false;
                              Filesystem.deleteFile({
                                path: filepath,
                                directory: Directory.Documents,
                              });
                            }
                          )
                          .catch(() => {
                            state.loading = false;

                            toasters.addWarning("Document was not printed");
                            Filesystem.deleteFile({
                              path: filepath,
                              directory: Directory.Documents,
                            });
                          });
                      });
                  })
                  .catch(() => {
                    state.loading = false;
                    toasters.addError(
                      "Could not create local PDF file for printing"
                    );
                  });
              };
            })
            .catch(() => {
              state.loading = false;
              toasters.addError("Could not initialize printing");
            });
        })
        .catch((err) => {
          state.loading = false;
          handleError(err);
        });
    })
    .catch((err) => {
      state.loading = false;
      handleError(err);
    });
};

const checkOutVisitor = () => {
  state.loading = true;
  if (state.checkInRegistration) {
    updateSessionRegistration({
      ...state.checkInRegistration,
      status: 3,
    })
      .then((response) => {
        state.loading = false;
        toasters.addSuccess("Visitor checked out");
        state.checkInRegistration = {
          ...state.checkInRegistration,
          ...response.data,
        };
        getVisitor();
      })
      .catch((err) => {
        state.loading = false;

        if (err.response.data.detail) {
          toasters.addError(err.response.data.detail);
        } else if (err.response.data._errors) {
          err.response.data._errors.forEach((error: any) => {
            toasters.addError(error.message);
          });
        } else {
          toasters.addError(
            "Could not check-in visitor, please try again later."
          );
        }
      });
  }
};
const checkInVisitor = () => {
  state.loading = true;
  if (state.checkInRegistration) {
    updateSessionRegistration({
      ...state.checkInRegistration,
      status: 2,
    })
      .then((response) => {
        toasters.addSuccess("Visitor checked in");
        state.checkInRegistration = {
          ...state.checkInRegistration,
          ...response.data,
        };
        state.loading = false;
        getVisitor();
      })
      .catch((err) => {
        state.loading = false;

        if (err.response.data.detail) {
          toasters.addError(err.response.data.detail);
        } else if (err.response.data._errors) {
          err.response.data._errors.forEach((error: any) => {
            toasters.addError(error.message);
          });
        } else {
          toasters.addError(
            "Could not check-in visitor, please try again later."
          );
        }
      });
  } else if (state.visitor.id) {
    sessionRegistration({
      session: session.id,
      status: 1,
      attending: state.visitor.id,
    })
      .then((response) => {
        state.loading = false;

        toasters.addSuccess("Visitor checked in");
        state.checkInRegistration = response.data;
        getVisitor();
      })
      .catch((err) => {
        state.loading = false;

        if (err.response.data.detail) {
          toasters.addError(err.response.data.detail);
        } else if (err.response.data._errors) {
          err.response.data._errors.forEach((error: any) => {
            toasters.addError(error.message);
          });
        } else {
          toasters.addError(
            "Could not check-in visitor, please try again later."
          );
        }
      });
  }
};

const getCheckinStatus = (status_id: number) => {
  let status_description = "";

  switch (status_id) {
    case 1: {
      status_description = "Registered";
      break;
    }
    case 2: {
      status_description = "Entered";
      break;
    }
    case 3: {
      status_description = "Checked out";
      break;
    }
    default: {
      status_description = "Registered";
      break;
    }
  }

  return status_description;
};

const dark_mode = computed(() => appStore.settings.theme === "dark");
</script>

<template>
  <ion-page>
    <ion-content>
      <div class="flex-desktop">
        <div :class="dark_mode ? 'full-width' : 'colored-bg full-width'">
          <div class="container">
            <ion-grid style="width: calc(100% + 110px)">
              <ion-row
                :style="isMobile ? '' : 'position: relative; right: 100px'"
              >
                <ion-fab-button
                  v-if="!isMobile"
                  class="fab-visitor"
                  color="primary"
                  @click="$router.go(-1)"
                >
                  <ion-icon :icon="arrowBack"></ion-icon>
                </ion-fab-button>
                <ion-col size="4" v-if="state.visitor.profile_picture">
                  <img :src="state.visitor.profile_picture" />
                </ion-col>
                <ion-col>
                  <h4 style="margin: 0">
                    {{ state.visitor.name }}
                  </h4>
                  <p>{{ state.visitor.email }}</p>
                  <ion-badge
                    color="registered"
                    v-if="state.visitor.checkin_status === 1"
                    >{{
                      getCheckinStatus(state.visitor.checkin_status)
                    }}</ion-badge
                  >
                  <ion-badge
                    color="entered"
                    v-if="state.visitor.checkin_status === 2"
                    >{{
                      getCheckinStatus(state.visitor.checkin_status)
                    }}</ion-badge
                  >
                  <ion-badge
                    color="checkedout"
                    v-if="state.visitor.checkin_status === 3"
                    >{{
                      getCheckinStatus(state.visitor.checkin_status)
                    }}</ion-badge
                  >
                </ion-col>
                <ion-col v-if="!isMobile">
                  <div
                    class="flex-desktop"
                    style="justify-content: end; align-items: center"
                  >
                    <div>
                      <ion-button
                        expand="block"
                        fill="clear"
                        color="dark"
                        v-if="
                          state.checkInRegistration &&
                          state.checkInRegistration.status === 2
                        "
                        @click="checkOutVisitor()"
                      >
                        <ion-icon slot="start" :icon="logOut"></ion-icon>
                        Check out
                      </ion-button>
                      <ion-button
                        v-else
                        expand="block"
                        fill="clear"
                        color="dark"
                        @click="checkInVisitor()"
                      >
                        <ion-icon slot="start" :icon="logIn"></ion-icon>
                        Check in
                      </ion-button>
                    </div>
                    <div>
                      <ion-button
                        expand="block"
                        fill="clear"
                        color="dark"
                        @click="printTag()"
                      >
                        <ion-icon slot="start" :icon="print"></ion-icon>
                        Print
                      </ion-button>
                    </div>
                    <div>
                      <ion-fab-button color="primary" size="small">
                        <ion-icon :icon="pencil"></ion-icon>
                      </ion-fab-button>
                    </div>
                    <div></div>
                  </div>
                </ion-col>
              </ion-row>
            </ion-grid>
            <ion-item>
              <ion-label> Birthdate </ion-label>
              <ion-label slot="end">
                {{ state.visitor.birthdate }}
              </ion-label>
            </ion-item>
            <ion-item>
              <ion-label> Job title </ion-label>
              <ion-label slot="end">
                {{ state.visitor.job_title }}
              </ion-label>
            </ion-item>
            <ion-item>
              <ion-label> Company </ion-label>
              <ion-label slot="end">
                {{ state.visitor.company }}
              </ion-label>
            </ion-item>
            <ion-item>
              <ion-label> Country </ion-label>
              <ion-label slot="end">
                {{ state.visitor.country }}
              </ion-label>
            </ion-item>
            <ion-item>
              <ion-label> Phone number </ion-label>
              <ion-label slot="end">
                {{ state.visitor.phone_number }}
              </ion-label>
            </ion-item>
          </div>
        </div>
      </div>
    </ion-content>

    <ion-fab
      v-if="isMobile"
      vertical="bottom"
      horizontal="center"
      slot="fixed"
      @click="
        router.push(
          `/event/${contextStore.event.id}/edit-visitor/${state.visitor.id}`
        )
      "
    >
      <ion-fab-button color="primary" id="edit-visitor">
        <ion-icon :icon="pencil"></ion-icon>
      </ion-fab-button>
    </ion-fab>

    <ion-footer v-if="isMobile">
      <ion-grid>
        <ion-row>
          <ion-col>
            <ion-button
              expand="block"
              fill="clear"
              color="dark"
              v-if="
                state.checkInRegistration &&
                state.checkInRegistration.status === 2
              "
              @click="checkOutVisitor()"
            >
              <ion-icon slot="start" :icon="logOut"></ion-icon>
              Check out
            </ion-button>
            <ion-button
              v-else
              expand="block"
              fill="clear"
              color="dark"
              @click="checkInVisitor()"
            >
              <ion-icon slot="start" :icon="logIn"></ion-icon>
              Check in
            </ion-button>
          </ion-col>
          <ion-col>
            <ion-button
              expand="block"
              fill="clear"
              color="dark"
              @click="printTag()"
            >
              <ion-icon slot="start" :icon="print"></ion-icon>
              Print
            </ion-button>
          </ion-col>
        </ion-row>
      </ion-grid>
    </ion-footer>

    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>

<style scoped lang="scss">
.colored-bg.full-width {
  overflow-y: auto;
  height: calc(100vh - 72px);
}

.fab-visitor {
  margin-right: 43px;
}
</style>
